import React from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';

// import loadable from '@loadable/component';

import { InputStepper } from '@vds/input-stepper';
import { Line } from '@vds/lines';
import { Body } from '@vds/typography';
import { TitleLockup } from '@vds/type-lockups';
import { TextLink } from '@vds/buttons';
import * as actionTypes from '../@Redux/actionTypes';

import { selectStateCache } from '../@selectors';
import { useCheckoutHooks } from '../@Redux/checkout.hooks';

import { DeviceAddOnContainer, DeviceContainer, Row, TilesContainer } from '../../../common/Cart/Tiles.styles';
import { Margin } from '../../../common/styles';
import { formatDotNPANXX, renderHTML } from '../../../common/Helpers';
import PriceInfo from '../Components/PriceInfo';
import ExtrasModal from '../../ExtrasModal';
import { getExpressCheckoutRequest } from '../modules/services/APIRequestBody';

import { checkoutLabels } from '../Components/labels';

const Accessories = () => {
  const stateCache = useSelector((state) => selectStateCache(state), shallowEqual);
  const dispatch = useDispatch();
  const { accessories, acctSubscriptions, depletionType, homePhone } = useCheckoutHooks(stateCache);

  const isCartPromos = window.vzFlags?.isCartPromos || false;

  const onQuantityClick = (value, device) => {
    const isIncrement = value > device.qty;
    const prodId = device.sorId ? device.sorId : device.productId;
    const pageName = 'editFromExpressCheckout';
    const updateType = isIncrement ? 'addToCart' : 'removeFromCart';

    let data;
    if (isIncrement) {
      data = {
        addAccessories: {
          prodId,
          qty: 1,
        },
        depletionType,
      };
      dispatch({
        type: actionTypes.EXPRESS_CHECKOUT_ADDREMOVE_ACCESSORY,
        data: { data, streamingDevice: 'streamingDevice', device, updateType, pageName, getExpressCheckoutRequest },
      });
    } else {
      data = {
        removeAccessories: {
          prodId,
          qty: 1,
        },
        depletionType,
      };

      dispatch({
        type: actionTypes.EXPRESS_CHECKOUT_ADDREMOVE_ACCESSORY,
        data: { data, streamingDevice: 'streamingDevice', device, updateType, pageName, getExpressCheckoutRequest },
      });
    }
  };

  const onRemoveClick = (device) => {
    const pageName = 'expressCheckout';
    const data = {
      removeYtTv: {
        prodId: device.itemId,
        pricePlanId: device.pricePlanId,
        price: device.price,
        name: device.displayName,
        recurringInd: device.recurringInd,
      },
    };
    dispatch({
      type: actionTypes.EXPRESS_CHECKOUT_ADD_REMOVE_YTTV,
      data: {
        ...data,
        streamingService: 'streamingService',
        pageName,
        getExpressCheckoutRequest,
        description: device.description,
        itemType: accessories.itemType,
      },
    });
  };

  const getDetailsContainer = (accessory) => {
    const { deviceDisplayName, planPrice, strikeOffPrice = '', itemType, qty } = accessory;

    return (
      <div>
        <Row width="100%">
          <TitleLockup
            data={{
              title: {
                primitive: 'p',
                bold: false,
                size: 'titleMedium',
                children: renderHTML(deviceDisplayName),
              },
              subtitle: {
                size: 'bodyLarge',
                children: <PriceInfo planPrice={planPrice} strikeOffPrice={strikeOffPrice} />,
              },
            }}
          />
        </Row>

        {itemType === 'ACCESSORY' && qty && (
          <Margin top="6px" bottom="6px">
            <InputStepper
              defaultValue={qty}
              maxValue={checkoutLabels.maximumQuantity}
              minValue={0}
              surface="light"
              trailingText=""
              width="auto"
              size="small"
              onChange={(value) => onQuantityClick(value, accessory)}
              ariaLabel={{
                decrement: `Decrease ${deviceDisplayName} by one from ${qty}`,
                increment: `Increase ${deviceDisplayName} by one from ${qty}`,
              }}
            />
          </Margin>
        )}
      </div>
    );
  };

  const getMiscellaneousContainer = (accessory) => {
    const miscList = [];
    const { itemType, activateText, activeToolTip } = accessory;

    if (itemType !== 'ACCESSORY') {
      if (activateText) {
        miscList.push({ title: activateText, desc: activeToolTip || '' });
      }
    }

    if (!miscList || miscList.length === 0) {
      return null;
    }

    const result = miscList.map((item, index) => {
      const { title, desc } = item;

      return (
        <DeviceAddOnContainer>
          <div>
            <Margin bottom="4px">
              <Body primitive="p" size="small" color="#6F7171">
                {title}
              </Body>
            </Margin>
            {desc && (
              <Row>
                <Body size="large" primitive="p">
                  {desc}
                </Body>
              </Row>
            )}
          </div>
          {index < miscList.length - 1 && (
            <Margin top="22px" bottom="22px">
              <Line type="secondary" />
            </Margin>
          )}
        </DeviceAddOnContainer>
      );
    });

    return result;
  };

  const renderSubscriptionsTiles = (item) => {
    if (!item || item.length === 0) {
      return null;
    }
    const result = item.map((accessory, index) => {
      const { itemId, imageUrl, planPrice, strikeOffPrice, isRemove, displayName } = accessory;

      return (
        <TilesContainer padding="24px" width="100%" key={itemId || index}>
          <DeviceContainer>
            <div className="detailsContainer">
              {imageUrl && (
                <div className="imageContent">
                  <img src={imageUrl} style={{ width: '95px' }} alt="" />
                </div>
              )}
              <TitleLockup
                data={{
                  title: {
                    primitive: 'p',
                    bold: false,
                    size: 'titleMedium',
                    children: renderHTML(displayName),
                  },
                  subtitle: {
                    size: 'bodyLarge',
                    children: <PriceInfo planPrice={planPrice} strikeOffPrice={strikeOffPrice} />,
                  },
                }}
              />
            </div>
            <div className="miscContainer">{getMiscellaneousContainer(accessory)}</div>
          </DeviceContainer>
          {isRemove && (
            <TextLink
              type="standAlone"
              role="button"
              data-testid="text_Link"
              size="small"
              data-track='{"type":"link","name":"remove|express checkout","data":{"page":{"throttle":"nsa version|vhi|ec y"},"target":{"sandBox":"FWA_Express_T_Checkout"}}}'  
              onClick={(e) => {
                e.preventDefault();
                onRemoveClick(accessory);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRemoveClick(accessory);
                }
              }}
            >
              Remove
            </TextLink>
          )}
        </TilesContainer>
      );
    });

    return result;
  };

  const renderPhoneTiles = (phoneDetails) => {
    const { formattedPlanName, planPrice, strikeOffPrice } = phoneDetails?.plan || {};
    const { npaNXX } = phoneDetails?.mtnDetails || {};

    return (
      <TilesContainer padding="24px" width="100%">
        <DeviceContainer>
          <div className="detailsContainer">
            <div className="rowContainer">
              {' '}
              <Row width="100%">
                <TitleLockup
                  data={{
                    title: {
                      primitive: 'p',
                      bold: false,
                      size: 'titleMedium',
                      children: renderHTML(formattedPlanName),
                    },
                    subtitle: {
                      size: 'bodyLarge',
                      children: <PriceInfo planPrice={planPrice} strikeOffPrice={strikeOffPrice} />,
                    },
                  }}
                />
              </Row>
            </div>
          </div>
        </DeviceContainer>
        <Margin top="10px" bottom="20px">
          {npaNXX && (
            <TextLink
              type="standAlone"
              role="button"
              data-testid="text_Link"
              size="small"
              data-track='{"type":"link","name":"remove|express checkout","data":{"page":{"throttle":"nsa version|vhi|ec y"},"target":{"sandBox":"FWA_Express_T_Checkout"}}}'  
              onClick={(e) => {
                e.preventDefault();
                onRemoveClick(accessories);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRemoveClick(accessories);
                }
              }}
            >
              {formatDotNPANXX(npaNXX)}
            </TextLink>
          )}
        </Margin>
        <TextLink
          type="standAlone"
          role="button"
          data-testid="text_Link"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            // onRemoveClick(accessory);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              // onRemoveClick(accessory);
            }
          }}
        >
          Remove
        </TextLink>
      </TilesContainer>
    );
  };

  const renderAccessoriesTiles = (accessoriesList) => {
    if (!accessoriesList || accessoriesList.length === 0) {
      return null;
    }

    const result = accessoriesList.map((accessory) => {
      const { imageUrl, strikeOffPrice } = accessory;

      return (
        <TilesContainer padding="24px" width="100%">
          <DeviceContainer>
            <div className="detailsContainer">
              {imageUrl && (
                <div className="imageContent">
                  <img src={imageUrl} style={{ width: '95px' }} alt="" />
                </div>
              )}

              <div className="rowContainer">{getDetailsContainer(accessory)}</div>
            </div>

            <div className="miscContainer">{getMiscellaneousContainer(accessory)}</div>
          </DeviceContainer>

          <div>
            {strikeOffPrice && (
              <Margin top="10px" bottom="4px">
                <Body primitive="p" size="medium">
                  {checkoutLabels.discountApplied}
                </Body>
              </Margin>
            )}
          </div>
        </TilesContainer>
      );
    });

    return result;
  };

  return (
    <React.Fragment>
      {renderAccessoriesTiles(accessories)}

      {renderSubscriptionsTiles(acctSubscriptions)}

      {homePhone && renderPhoneTiles(homePhone)}

      <div>{isCartPromos && <ExtrasModal />}</div>
    </React.Fragment>
  );
};

export default Accessories;
